import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { MODULE_DEFS } from "../lib/modules";
import { renderMarkdown } from "../lib/markdown";
import { Search, Trash2, Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CaseHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterSlug, setFilterSlug] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/consultations", {
        params: { q: q || undefined, module_slug: filterSlug || undefined },
      });
      setItems(r.data);
    } catch (_) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => items, [items]);

  const del = async (id) => {
    if (!window.confirm("Delete this consultation?")) return;
    await api.delete(`/consultations/${id}`);
    setItems((prev) => prev.filter((i) => i.id !== id));
    if (selected?.id === id) setSelected(null);
    toast.success("Deleted");
  };

  return (
    <Layout>
      <div className="kicker">Case History</div>
      <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2" data-testid="history-title">
        Your consultations
      </h1>
      <p className="mt-2 text-sm text-[var(--pa-text-muted)]">
        Search across every AI consultation you've generated.
      </p>

      <div className="gold-divider my-8" />

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-3 text-[var(--pa-text-dim)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                data-testid="history-search"
                className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm pl-9 pr-3 py-2 focus:border-[var(--pa-gold)] focus:outline-none"
              />
            </div>
            <select
              value={filterSlug}
              onChange={(e) => setFilterSlug(e.target.value)}
              data-testid="history-filter"
              className="bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm px-2 py-2"
            >
              <option value="">All modules</option>
              {Object.entries(MODULE_DEFS).map(([s, d]) => (
                <option key={s} value={s}>{d.name}</option>
              ))}
            </select>
            <button
              onClick={load}
              data-testid="history-apply"
              className="bg-[var(--pa-gold)] text-[var(--pa-bg)] text-sm font-semibold rounded-md px-4 hover:bg-[var(--pa-gold-hover)] transition"
            >
              Go
            </button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-[var(--pa-text-muted)]">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-[var(--pa-border)] rounded-lg p-8 text-center text-sm text-[var(--pa-text-muted)]">
              No consultations yet. Start one from the Dashboard.
            </div>
          ) : (
            <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {filtered.map((it) => (
                <li
                  key={it.id}
                  data-testid={`history-item-${it.id}`}
                  className={`border rounded-md p-4 cursor-pointer transition ${
                    selected?.id === it.id
                      ? "border-[var(--pa-gold)] bg-[var(--pa-surface)]"
                      : "border-[var(--pa-border)] bg-[var(--pa-surface)] hover:border-[var(--pa-border-active)]"
                  }`}
                  onClick={() => setSelected(it)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-serif text-base text-[var(--pa-text)] leading-snug">
                      {it.module_name}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        del(it.id);
                      }}
                      data-testid={`history-delete-${it.id}`}
                      className="text-[var(--pa-text-dim)] hover:text-red-400 transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-[var(--pa-text-dim)] mt-1">
                    {new Date(it.created_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-[var(--pa-text-muted)] mt-2 line-clamp-2">
                    {it.response.replace(/[#*`]/g, "").slice(0, 140)}…
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="lg:col-span-7" data-testid="history-detail-pane">
          {!selected ? (
            <div className="bg-[var(--pa-surface)] border border-dashed border-[var(--pa-border)] rounded-lg p-10 text-center">
              <Eye size={28} className="text-[var(--pa-gold)] mx-auto" />
              <div className="kicker mt-4">Detail view</div>
              <h3 className="font-serif text-xl text-[var(--pa-text)] mt-1">
                Select a consultation to view
              </h3>
            </div>
          ) : (
            <div className="bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg p-6">
              <div className="kicker">Consultation</div>
              <div className="font-serif text-xl text-[var(--pa-text)] mt-1">
                {selected.module_name}
              </div>
              <div className="text-xs text-[var(--pa-text-dim)] mt-1">
                {new Date(selected.created_at).toLocaleString()}
              </div>
              <Link
                to={`/consultation/${selected.id}`}
                data-testid="open-detail"
                className="inline-block mt-3 text-xs text-[var(--pa-gold)] hover:underline"
              >
                Open full view & request specialist review →
              </Link>
              <div className="gold-divider my-5" />
              <div className="kicker mb-2">Inputs</div>
              <ul className="text-xs text-[var(--pa-text-muted)] space-y-1 mb-6">
                {Object.entries(selected.inputs).map(([k, v]) => (
                  <li key={k}>
                    <span className="text-[var(--pa-text)]">{k.replace(/_/g, " ")}:</span>{" "}
                    {String(v)}
                  </li>
                ))}
              </ul>
              <div className="kicker mb-2">Specialist Analysis</div>
              <div
                className="prose-clinical"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(selected.response) }}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
